import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { countryCodes } from '../constants/countryCodes';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';


const Profile = () => {
  const { user, refreshUser, checkProfileStatus } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    postalcode: '',
    address: '',
    sendInvoiceToClient: false,
    trackMileage: 0,
    sosNumber:'',
    sosCountryCode:""
  });
  const [profileError, setProfileError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [postalCodeAutoFilled, setPostalCodeAutoFilled] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState('');
  const [sosNumberError, setSosNumberError] = useState('');
  const addressTimeoutRef = useRef(null);

  // Helper function to get ISO country code from countryCodes array
  const getISOFromCountryCode = (code) => {
    // Find matching entry in countryCodes array
    const codeWithoutPlus = code.replace(/^\+/, '').trim();
    const countryEntry = countryCodes.find(cc => cc.code.replace(/^\+/, '').trim() === codeWithoutPlus);
    
    // Return ISO code directly from countryCodes array
    return countryEntry?.iso || null;
  };

  // US Postal Code validation function
  const validateUSPostalCode = (postalCode) => {
    if (!postalCode || postalCode.trim() === '') return { isValid: true, message: '' }; // Empty is OK for required field validation
    
    // Remove spaces and convert to uppercase
    const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();
    
    // US ZIP code patterns:
    // 5 digits: 12345
    // 5 digits + hyphen + 4 digits (ZIP+4): 12345-6789
    const usZipPattern = /^(\d{5})(-\d{4})?$/;

    if (usZipPattern.test(cleaned)) {
      return { isValid: true, message: '' };
    } else {
      return { 
        isValid: false, 
        message: 'Invalid US postal code format. Use 12345 or 12345-6789' 
      };
    }
  };

  // SOS Number validation based on country code
  const validateSOSNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber || !phoneNumber.trim()) {
      return { isValid: true, message: '' }; // Empty is OK for required field validation
    }
    
    if (!countryCode || !countryCode.trim()) {
      return { isValid: true, message: '' }; // No validation if country code not selected
    }

    try {
      // Get ISO country code from countryCodes array
      const isoCountry = getISOFromCountryCode(countryCode);
      
      // Build full number with country code prefix
      const fullNumber = `${countryCode}${phoneNumber}`;
      
      if (isoCountry) {
        // Validate with ISO country code for better accuracy
        const isValid = isValidPhoneNumber(fullNumber, isoCountry);
        if (isValid) {
          return { isValid: true, message: '' };
        } else {
          return {
            isValid: false,
            message: `Invalid phone number format for ${countryCode}. Please enter a valid number.`
          };
        }
      } else {
        // For countries not in map, try validation without ISO (fallback)
        try {
          const isValid = isValidPhoneNumber(fullNumber);
          return {
            isValid,
            message: isValid ? '' : `Invalid phone number format for ${countryCode}`
          };
        } catch (e) {
          // If library can't parse, allow but show warning
          return {
            isValid: true,
            message: ''
          };
        }
      }
    } catch (error) {
      // If validation fails, allow input but show warning
      return {
        isValid: false,
        message: `Please check the phone number format for ${countryCode}`
      };
    }
  };

  useEffect(() => {
    if (user) {
      const newFormData = {
        username: user.username || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        companyName: user.company_name || '',
        phone: user.phone || '',
        postalcode: user.postal_code || '',
        address: user.address || '',
        sendInvoiceToClient: user.send_invoice_status_client === 1,
        trackMileage: user.track_mileage || user.mileage_status,
        sosNumber: user.sos_number || '',
        sosCountryCode: user.sos_country_code || ''
      };
      setFormData(newFormData);
      
      // Validate SOS number if both are present when loading from user
      if (newFormData.sosNumber && newFormData.sosNumber.trim() && 
          newFormData.sosCountryCode && newFormData.sosCountryCode.trim()) {
        const validation = validateSOSNumber(newFormData.sosNumber, newFormData.sosCountryCode);
        if (!validation.isValid) {
          setSosNumberError(validation.message);
        } else {
          setSosNumberError('');
        }
      } else {
        setSosNumberError('');
      }
    }
    handleProfileStatusCheck();
  }, [user]);

  const handleProfileStatusCheck = async () => {
    try {
      const response = await api.get('/v1/auth/check-profile');
      if (response.data.data.message === 'incomplete') {
        setProfileError('* Complete profile information to see other features of the platform');
      }
    } catch (error) {
      console.error('Error checking profile status:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Validate US postal code when manually changed
    if (name === 'postalcode') {
      const validation = validateUSPostalCode(value);
      if (!validation.isValid && value.trim() !== '') {
        setPostalCodeError(validation.message);
        // Show validation warning but don't block input
        setTimeout(() => {
          enqueueSnackbar(validation.message, { variant: 'warning', autoHideDuration: 3000 });
        }, 500);
      } else {
        setPostalCodeError('');
      }
      // Reset auto-filled flag if user manually changes
      if (postalCodeAutoFilled) {
        setPostalCodeAutoFilled(false);
      }
    }
    
    // Validate SOS number when it changes or country code changes
    if (name === 'sosNumber' || name === 'sosCountryCode') {
      const currentSosNumber = name === 'sosNumber' ? value : formData.sosNumber;
      const currentCountryCode = name === 'sosCountryCode' ? value : formData.sosCountryCode;
      
      if (currentSosNumber && currentSosNumber.trim() && currentCountryCode && currentCountryCode.trim()) {
        const validation = validateSOSNumber(currentSosNumber, currentCountryCode);
        if (!validation.isValid) {
          setSosNumberError(validation.message);
        } else {
          setSosNumberError('');
        }
      } else {
        setSosNumberError('');
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      address: value
    }));

    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    } 
    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
       const res = await fetch(
      `${process.env.REACT_APP_API_URL}/v1/employees/places/autocomplete?input=${encodeURIComponent(value)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

        const data = await res.json();
        setAddressSuggestions(data.data.predictions || []);
        setShowSuggestions(true);
      } catch (err) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleAddressSelect = async (suggestion) => {
    try {
      // First update the address field
      setFormData(prev => ({
        ...prev,
        address: suggestion.description
      }));
      setAddressSuggestions([]);
      setShowSuggestions(false);

      // Then fetch detailed place information including postal code
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/employees/places/details?place_id=${suggestion.place_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const placeDetails = await response.json();
      
      if (placeDetails.data && placeDetails.data.postal_code) {
        // Validate US postal code format
        const postalCode = placeDetails.data.postal_code;
        const validation = validateUSPostalCode(postalCode);
        
        // Update postal code if available (update even if invalid, but show warning)
        setFormData(prev => ({
          ...prev,
          postalcode: postalCode
        }));
        setPostalCodeAutoFilled(true);
        
        if (validation.isValid) {
          setPostalCodeError('');
          enqueueSnackbar('Address and postal code updated automatically', { variant: 'success' });
        } else {
          setPostalCodeError(validation.message);
          enqueueSnackbar('Address updated. Postal code may need manual correction.', { variant: 'warning' });
        }
      } else {
        setPostalCodeAutoFilled(false);
        enqueueSnackbar('Address updated. Please enter postal code manually if needed.', { variant: 'info' });
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      // Still update the address even if postal code fetch fails
      setFormData(prev => ({
        ...prev,
        address: suggestion.description
      }));
      setAddressSuggestions([]);
      setShowSuggestions(false);
      enqueueSnackbar('Address updated. Please enter postal code manually.', { variant: 'warning' });
    }
  };

  const validateForm = () => {
    if (!formData.username) {
      enqueueSnackbar('Username field should not be empty', { variant: 'error' });
      return false;
    }
    if (!formData.firstName) {
      enqueueSnackbar('First name field should not be empty', { variant: 'error' });
      return false;
    }
    if (!formData.lastName) {
      enqueueSnackbar('Last name field should not be empty', { variant: 'error' });
      return false;
    }
    if (!formData.postalcode) {
      enqueueSnackbar('Postal code field should not be empty', { variant: 'error' });
      return false;
    }
    if (!formData.phone) {
      enqueueSnackbar('Phone field should not be empty', { variant: 'error' });
      return false;
    }
    // if (formData.phone.length < 9) {
    //   enqueueSnackbar('Phone length should be at least 10 digits', { variant: 'error' });
    //   return false;
    // }

    // ✅ U.S. Phone Validation (no length check)
const usPhoneRegex =
  /^(?:\+1\s?)?\(?([2-9][0-9]{2})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

if (!usPhoneRegex.test(formData.phone)) {
  enqueueSnackbar(
    'Invalid U.S. contact number.\nPlease enter a valid phone number in one of these formats:\n\n' +
      '• (212) 456-7890\n' +
      '• 212-456-7890\n' +
      '• 212.456.7890\n' +
      '• +1 212-456-7890',
    {
      variant: 'error',
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 6000,
    }
  );
  return;
}

    if (!formData.companyName) {
      enqueueSnackbar('Company name field should not be empty', { variant: 'error' });
      return false;
    }
    if (!formData.address) {
      enqueueSnackbar('Address field should not be empty', { variant: 'error' });
      return false;
    }
    
    // SOS validation - if country code is selected, SOS number is required
    if (formData.sosCountryCode && !formData.sosNumber) {
      enqueueSnackbar('SOS number is required when country code is selected', { variant: 'error' });
      return false;
    }
    
    // SOS validation - if SOS number is entered, country code is required
    if (formData.sosNumber && !formData.sosCountryCode) {
      enqueueSnackbar('SOS country code is required when SOS number is entered', { variant: 'error' });
      return false;
    }
    
    // SOS validation - validate phone number pattern if both are present
    if (formData.sosNumber && formData.sosCountryCode && formData.sosNumber.trim()) {
      const validation = validateSOSNumber(formData.sosNumber, formData.sosCountryCode);
      if (!validation.isValid) {
        enqueueSnackbar(validation.message || 'Invalid SOS phone number format', { variant: 'error' });
        return false;
      }
    }
    
    return true;
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try { 
      const response = await api.put('/v1/users/profile', {
        user_name: formData.username,
        first_name: formData.firstName,
        last_name: formData.lastName,
        postal_code: formData.postalcode,
        phone: formData.phone,
        company_name: formData.companyName,
        address: formData.address,
        email: user.email,
        send_invoice_status_client: formData.sendInvoiceToClient ? 1 : 0,
        mileage_status: formData.trackMileage ? 1 : 0,
        sos_number:formData.sosNumber,
        sos_country_code:formData.sosCountryCode
      });
      
      // Show success immediately if response is successful (200 OK or has user object)
      const isSuccess = response.status === 200 || response.data?.user || 
                        response.data?.message?.toLowerCase().includes('success');
      
      if (isSuccess) {
        // Show success message immediately
        enqueueSnackbar(response.data?.message || 'Profile updated successfully', { variant: 'success' });
        
        // Set loading to false immediately after showing success
        setLoading(false);
        
        // Refresh user data and profile status in background (fire and forget)
        refreshUser().catch(err => console.error('Error refreshing user:', err));
        checkProfileStatus().catch(err => console.error('Error checking profile status:', err));
      } else {
        setLoading(false);
        enqueueSnackbar(response.data?.message || 'Error updating profile', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setLoading(false);
      enqueueSnackbar(error.response?.data?.message || 'Error updating profile', { variant: 'error' });
    }
  };
  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      enqueueSnackbar('All password fields are required', { variant: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      enqueueSnackbar('New password and confirm password should match', { variant: 'error' });
      return;
    }

    const passwordRegex = /^(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      enqueueSnackbar('Password must contain at least 1 number, 1 special character and minimum 8 characters', { variant: 'error' });
      return;
    }

    try {
      await api.post('/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      enqueueSnackbar('Password changed successfully', { variant: 'success' });
      setOpenPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Error changing password', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          fontWeight: '700',
          color: '#4e0159',
          letterSpacing: '-0.5px',
          mb: 0
        }}>
          Profile
        </Typography>
          {profileError && (
            <Typography color="error" sx={{ mr: 2 }}>
              {profileError}
            </Typography>
          )}
        <Box>
          <Button
            variant="contained"
            href="/subscriptions"
            sx={{
              backgroundColor: '#4e0159',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#4e0159',
                opacity: 0.9
              }
            }}
          >
            Subscription
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, width: { xs: '100%', md: '60%' }, margin: '0 auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Profile Settings</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {user?.email}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSaveProfile}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Postal Code"
                name="postalcode"
                value={formData.postalcode}
                onChange={handleChange}
                error={!!postalCodeError}
                helperText={postalCodeError || (postalCodeAutoFilled ? "Auto-filled from address selection" : "")}
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: postalCodeError ? 'error.main' : (postalCodeAutoFilled ? 'success.main' : 'text.secondary')
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <div style={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  required
                  label="Address (include city and state)"
                  name="address"
                  value={formData.address}
                  onChange={handleAddressChange}
                  multiline
                  autoComplete="off"
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <ul style={{
                    position: 'absolute',
                    background: '#fff',
                    border: '1px solid #ccc',
                    width: '100%',
                    zIndex: 10,
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}>
                    {addressSuggestions.map((s) => (
                      <li
                        key={s.place_id}
                        onClick={() => handleAddressSelect(s)}
                        style={{ padding: '8px', cursor: 'pointer' }}
                      >
                        {s.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>SOS Country Code</InputLabel>
                <Select
                  name="sosCountryCode"
                  value={formData.sosCountryCode}
                  onChange={handleChange}
                  label="SOS Country Code"
                >
                  <MenuItem value="">
                    <em>Select a country code</em>
                  </MenuItem>
                  {countryCodes.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.code} - {country.country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SOS Number"
                name="sosNumber"
                value={formData.sosNumber}
                onChange={handleChange}
                disabled={!formData.sosCountryCode}
                error={!!sosNumberError}
                helperText={sosNumberError || (!formData.sosCountryCode ? "Please select a country code first." : "")}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.sendInvoiceToClient}
                      onChange={handleChange}
                      name="sendInvoiceToClient"
                      sx={{
                        color: '#4e0159',
                        '&.Mui-checked': {
                          color: '#4e0159'
                        }
                      }}
                    />
                  }
                  label="Send Invoice to Client"
                />
                <Tooltip title="When checkbox is checked then invoice will be sent to the client otherwise will be sent to the company">
                  <IconButton size="small">
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                Toggle to the right to track mileage
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.trackMileage}
                    onChange={handleChange}
                    name="trackMileage"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4e0159',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4e0159',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: 'rgba(78, 1, 89, 0.3)',
                      }
                    }}
                  />
                }
                label={formData.trackMileage ? "On (active)" : "Off (inactive)"}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{ 
                  mr: 2,
                  backgroundColor: '#4e0159',
                  '&:hover': {
                    backgroundColor: '#4e0159',
                    opacity: 0.9
                  }
                }}
              >
                Save Profile
              </Button>
              <Button
                variant="contained"
                onClick={() => setOpenPasswordDialog(true)}
                sx={{
                  backgroundColor: '#4e0159',
                  '&:hover': {
                    backgroundColor: '#4e0159',
                    opacity: 0.9
                  }
                }}
              >
                Change Password
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Password Change Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                name="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenPasswordDialog(false)} 
            sx={{
              color: '#4e0159',
              '&:hover': {
                color: '#4e0159',
                opacity: 0.8,
                backgroundColor: 'rgba(78, 1, 89, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            sx={{
              backgroundColor: '#4e0159',
              '&:hover': {
                backgroundColor: '#4e0159',
                opacity: 0.9
              }
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
